import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ManhwaCard from '@/components/feature/ManhwaCard';
import JsonLD from '@/components/feature/JsonLD';
import { manhwaList } from '@/mocks/manhwa';
import { categories } from '@/mocks/categories';

export default function Article() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  const article = manhwaList.find(m => m.id === Number(id));

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center">
              <i className="ri-error-warning-line text-5xl text-foreground-300"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">漫画未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您要找的漫画不存在或已被移除。</p>
            <Link
              to="/category/all"
              className="inline-block px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full font-medium text-sm transition-colors whitespace-nowrap"
            >
              浏览全部漫画
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const catInfo = categories.find(c => c.slug === article.category);
  const relatedArticles = manhwaList
    .filter(m => m.category === article.category && m.id !== article.id)
    .slice(0, 4);

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const articleJsonLD = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${article.title} - 韩漫之家`,
    description: article.description,
    author: { '@type': 'Person', name: article.author },
    datePublished: '2024-01-15',
    dateModified: article.updatedAt,
    url: `http://topshine.net.cn/article/${article.id}`,
    publisher: { '@type': 'Organization', name: '韩漫之家' },
    image: article.cover,
  };

  const faqJsonLD = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `《${article.title}》是什么类型的韩国漫画？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: `《${article.title}》是一部${catInfo?.name || ''}类型的韩国漫画，由${article.author}创作。目前状态为${article.status}，已更新至${article.chapters}话。`,
        },
      },
      {
        '@type': 'Question',
        name: '如何在韩漫之家在线阅读韩国漫画？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫之家是免费韩漫大全平台，您可以直接访问我们的网站浏览好看的韩国漫画。我们提供稳定的漫画网址和优质的韩漫在线阅读服务。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫之家的漫画更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫之家每日同步更新大量韩国漫画内容，确保读者能够第一时间阅读到最新的韩漫章节。我们的服务稳定值得信赖。',
        },
      },
    ],
  };

  return (
    <>
      <JsonLD data={[articleJsonLD, faqJsonLD]} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: catInfo?.name || '分类', href: `/category/${article.category}` },
              { label: article.title },
            ]} />

            {/* Article Header */}
            <article className="mt-4 mb-12">
              <div className="flex flex-col lg:flex-row gap-8">
                {/* Cover */}
                <div className="flex-shrink-0">
                  <div className="w-48 md:w-56 h-72 md:h-80 rounded-xl overflow-hidden bg-background-200">
                    <img
                      src={article.cover}
                      alt={article.title}
                      title={`${article.title} - 韩漫在线阅读`}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-3 flex-wrap">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                      article.status === '连载中'
                        ? 'bg-accent-500 text-background-50'
                        : 'bg-foreground-600 text-background-50'
                    }`}>
                      {article.status}
                    </span>
                    <Link
                      to={`/category/${article.category}`}
                      className="inline-block px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium hover:bg-secondary-200 transition-colors"
                    >
                      {catInfo?.name}
                    </Link>
                  </div>

                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                    {article.title}
                  </h1>

                  <p className="text-sm text-foreground-500 mb-4">
                    作者：<strong className="text-foreground-800">{article.author}</strong>
                  </p>

                  <div className="flex flex-wrap items-center gap-4 mb-5">
                    <div className="flex items-center gap-1.5">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-star-fill text-accent-400 text-sm"></i>
                      </span>
                      <span className="text-lg font-bold text-accent-600">{article.rating}</span>
                      <span className="text-xs text-foreground-500">分</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-foreground-500">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-eye-line text-sm"></i>
                      </span>
                      {formatViews(article.views)} 次阅读
                    </div>
                    <div className="flex items-center gap-1.5 text-sm text-foreground-500">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-file-list-3-line text-sm"></i>
                      </span>
                      共 {article.chapters} 话
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="inline-block px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs font-medium"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Description */}
                  <div className="bg-background-100 rounded-xl p-5 mb-6">
                    <h2 className="text-sm font-semibold text-foreground-800 mb-2">作品简介</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {article.description}
                    </p>
                  </div>

                  {/* CTA */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full font-medium text-sm transition-colors whitespace-nowrap flex items-center justify-center gap-2">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-play-circle-line text-sm"></i>
                      </span>
                      开始阅读
                    </button>
                    <button className="px-8 py-3 bg-background-100 hover:bg-background-200 text-foreground-700 rounded-full font-medium text-sm transition-colors whitespace-nowrap flex items-center justify-center gap-2">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-bookmark-line text-sm"></i>
                      </span>
                      加入收藏
                    </button>
                  </div>
                </div>
              </div>
            </article>

            {/* Chapter List Preview */}
            <section className="mb-12" aria-labelledby="chapters-heading">
              <h2 id="chapters-heading" className="text-lg font-heading font-bold text-foreground-900 mb-4">
                章节列表
              </h2>
              <div className="bg-background-100 rounded-xl p-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {Array.from({ length: Math.min(12, article.chapters) }, (_, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between px-4 py-2.5 rounded-lg hover:bg-background-50 transition-colors cursor-pointer"
                    >
                      <span className="text-sm text-foreground-700">
                        第 {article.chapters - i} 话
                      </span>
                      <span className="text-xs text-foreground-400">
                        {i === 0 ? article.updatedAt : '2026-07-01'}
                      </span>
                    </div>
                  ))}
                </div>
                {article.chapters > 12 && (
                  <div className="text-center mt-4 pt-4 border-t border-background-200/70">
                    <span className="text-sm text-primary-600 font-medium cursor-pointer hover:text-primary-700 transition-colors">
                      查看全部 {article.chapters} 话
                    </span>
                  </div>
                )}
              </div>
            </section>

            {/* Related */}
            <section className="mb-12" aria-labelledby="related-heading">
              <h2 id="related-heading" className="text-lg font-heading font-bold text-foreground-900 mb-4">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {relatedArticles.map((m) => (
                  <ManhwaCard
                    key={m.id}
                    id={m.id}
                    title={m.title}
                    author={m.author}
                    cover={m.cover}
                    rating={m.rating}
                    views={m.views}
                    status={m.status}
                    category={m.category}
                    tags={m.tags}
                    chapters={m.chapters}
                    size="sm"
                  />
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section className="mb-12" aria-labelledby="faq-heading">
              <h2 id="faq-heading" className="text-lg font-heading font-bold text-foreground-900 mb-4">
                常见问题
              </h2>
              <div className="space-y-3">
                <details className="bg-background-100 rounded-xl p-5 group">
                  <summary className="text-sm font-medium text-foreground-800 cursor-pointer list-none flex items-center justify-between">
                    《{article.title}》是什么类型的韩国漫画？
                    <span className="w-5 h-5 flex items-center justify-center transition-transform group-open:rotate-180">
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </summary>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                    《{article.title}》是一部{catInfo?.name}类型的韩国漫画，由{article.author}创作。目前状态为{article.status}，已更新至{article.chapters}话，深受广大韩漫爱好者的喜爱。
                  </p>
                </details>

                <details className="bg-background-100 rounded-xl p-5 group">
                  <summary className="text-sm font-medium text-foreground-800 cursor-pointer list-none flex items-center justify-between">
                    如何在韩漫之家在线阅读韩国漫画？
                    <span className="w-5 h-5 flex items-center justify-center transition-transform group-open:rotate-180">
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </summary>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                    韩漫之家是免费韩漫大全平台，您可以直接访问我们的网站浏览好看的韩国漫画。我们提供稳定的漫画网址和优质的韩漫在线阅读服务，让您随时随地享受阅读乐趣。
                  </p>
                </details>

                <details className="bg-background-100 rounded-xl p-5 group">
                  <summary className="text-sm font-medium text-foreground-800 cursor-pointer list-none flex items-center justify-between">
                    韩漫之家的漫画更新频率如何？
                    <span className="w-5 h-5 flex items-center justify-center transition-transform group-open:rotate-180">
                      <i className="ri-arrow-down-s-line"></i>
                    </span>
                  </summary>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                    韩漫之家每日同步更新大量韩国漫画内容，确保读者能够第一时间阅读到最新的韩漫章节。我们的服务稳定值得信赖，是您在线阅读韩漫的最佳选择。
                  </p>
                </details>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}